import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import { useListValidationTypes } from 'src/features/validationTypes/api/useListValidationTypes';
import CreateValidationTypeFormModal from 'src/features/validationTypes/components/forms/CreateValidationTypeFormModal';
import CellValidationTypeActions from 'src/features/validationTypes/components/tables/CellValidationTypeActions';
import { validationTypeBadgeOptions } from 'src/features/validationTypes/utils/constants';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { VALIDATION_TYPE, ValidationType } from 'src/features/validationTypes/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { generalMessages } from 'src/registry/utils/messages';
import { booleanBadgeOptions } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { membershipMessages } from 'src/features/membership/utils/messages';

function ValidationTypesTable() {
    const { formatMessage, formatNumber } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();

    const permissionManageValidation = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            ...(accountId && { validator_account: accountId }),
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, queryPaginationOptions, accountId, parkingId],
    );

    const { data: validationTypes, isLoading } = useListValidationTypes(payload);

    const columnHelper = createColumnHelper<ValidationType>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="font-medium">{row.original.name}</div>
                        <div>{row.original.code}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(validationTypesMessages.type),
                badgeOptions: validationTypeBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const validationType: VALIDATION_TYPE = row.original.type;
                const validationAmount = row.original.amount ?? 0;

                const typeCentsAmount =
                    validationType === VALIDATION_TYPE.CHARGE &&
                    formatNumber(validationAmount / 100, {
                        style: 'currency',
                        currency: row.original.currency,
                    });

                const typeMinutesAmount = validationAmount / 60;

                let displayAmount;
                switch (validationType) {
                    case VALIDATION_TYPE.CHARGE:
                        displayAmount = typeCentsAmount;
                        break;
                    case VALIDATION_TYPE.GUEST:
                        displayAmount = '-';
                        break;
                    default:
                        displayAmount = `${formatMessage(commonMessages.minutes, { value: typeMinutesAmount })}`;
                }

                return displayAmount;
            },
            meta: {
                title: formatMessage(validationTypesMessages.validationTypeAmount),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(membershipMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
            id: 'is_active',
        }),
        columnHelper.accessor('can_be_stacked', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(validationTypesMessages.canBeStacked),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('can_be_combined', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(validationTypesMessages.canBeCombined),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageValidation
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellValidationTypeActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ValidationType, any>
            isLoading={isLoading}
            columns={columns}
            data={validationTypes}
            search={search}
            filters={filters}
            pagination={pagination}
            semantics={{
                title: formatMessage(validationTypesMessages.validationTypes),
                entryName: formatMessage(validationTypesMessages.type).toLowerCase(),
                entryNames: formatMessage(validationTypesMessages.types).toLowerCase(),
                placeholder: formatMessage(generalMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageValidation && { newEntry: CreateValidationTypeFormModal }),
            }}
        />
    );
}

export default ValidationTypesTable;
